import { allMusicServices, musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { linkTypeForUrl } from '../profile/linkTypeForUrl';

/** One row of the links editor: the service, and what to call it. */
export interface CatalogLinkService {
  integrationType: string;
  name: string;
}

/**
 * Every service a release can be filed under, the ones it is already on first.
 *
 * The order matters more than it looks: the artist opened this to check a
 * match, so the services that claim to hold the release belong at the top,
 * where a wrong one is seen instead of scrolled past. Everything else follows
 * so a missing link can be added without leaving the sheet.
 *
 * A service FYM no longer supports is only listed when the row already carries
 * a link for it. Drawing an empty row for a service nobody can reach would
 * invite the artist to fill it in.
 */
export function catalogLinkServices(serviceLinks: Record<string, string> | null): CatalogLinkService[] {
  const filled = new Set(Object.keys(serviceLinks ?? {}));
  const listed = allMusicServices
    .filter((service) => service.musicAPIID !== undefined && (service.isSupported || filled.has(service.musicAPIID)))
    .map((service) => ({ integrationType: service.musicAPIID as string, name: service.name }));

  // A key musicapi sent that no service here answers for. Rare, but dropping it
  // silently would lose a link the artist can see on the row.
  const unknown = [...filled]
    .filter((integrationType) => musicServicesByMusicAPIID[integrationType] === undefined)
    .map((integrationType) => ({ integrationType, name: integrationType }));

  const all = [...listed, ...unknown];
  return [...all.filter((s) => filled.has(s.integrationType)), ...all.filter((s) => !filled.has(s.integrationType))];
}

/**
 * Whether a typed address plainly belongs somewhere else.
 *
 * The same recognition the server refuses on, so the warning appears while the
 * artist types instead of after they press Save. An empty field is not a
 * mistake — it is how a wrong link is removed.
 */
export function isServiceLinkMismatched(integrationType: string, url: string): boolean {
  const trimmed = url.trim();
  if (trimmed === '') {
    return false;
  }
  const expected = musicServicesByMusicAPIID[integrationType]?.linkType;
  if (expected === undefined) {
    return false;
  }
  return linkTypeForUrl(trimmed) !== expected;
}
